/**
 * TanStack Query hooks for the brand registry (see shared/api/registry-api.ts).
 * Follows the same shape as features/admin/hooks/useAdminUsers.ts.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { registryApi, type MapMissInput } from '@/shared/api/registry-api';

const PROGRAMS_KEY = ['registry', 'programs'];
const BRANDS_KEY = ['registry', 'brands'];
const MISSES_KEY = ['registry', 'misses'];
const BRAND_CARD_KEY = (idOrSlug: string) => ['registry', 'brand-card', idOrSlug];

/**
 * GET /brands/:idOrSlug -- open to any authenticated role, access-filtered server-side.
 * Disabled until an id/slug is known (e.g. route param not yet resolved).
 */
export function useBrandCard(idOrSlug: string | undefined) {
  return useQuery({
    queryKey: BRAND_CARD_KEY(idOrSlug ?? ''),
    queryFn: () => registryApi.getBrandCard(idOrSlug as string),
    enabled: !!idOrSlug,
    staleTime: 1000 * 30,
    retry: false, // 403/404 are expected outcomes here, not transient failures
  });
}

/**
 * Admin-only. `enabled` lets callers skip the request entirely for non-admin sessions
 * instead of eating a 403 (mirrors useUnmappedMisses below).
 */
export function usePrograms(enabled = true) {
  return useQuery({
    queryKey: PROGRAMS_KEY,
    queryFn: () => registryApi.listPrograms(),
    enabled,
    staleTime: 1000 * 60,
  });
}

/** Admin-only. Every registry brand, including ones with zero events -- used by the brand
 * picker and for computing per-program brand counts client-side. */
export function useRegistryBrands(enabled = true) {
  return useQuery({
    queryKey: BRANDS_KEY,
    queryFn: () => registryApi.listBrands(),
    enabled,
    staleTime: 1000 * 60,
  });
}

/** Admin-only. `enabled = false` for non-admin sessions (e.g. the sidebar badge check). */
export function useUnmappedMisses(enabled = true) {
  return useQuery({
    queryKey: MISSES_KEY,
    queryFn: () => registryApi.listMisses(),
    enabled,
    staleTime: 1000 * 30,
  });
}

export function useMapMiss() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: MapMissInput) => registryApi.mapMiss(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MISSES_KEY });
    },
  });
}
